import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { AssessmentType, AssessmentTypeDescriptions, AuthRoles, Course, Gender, GenderDescriptions } from '../../models/enums/enums';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { countryResponse } from '../../models/country/countryResponse';
import { UtilityService } from '../../services/utility/utility.service';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { StudentListForSuperAdminRequest } from '../../models/super-admin/studentListForSuperAdminRequest';
import { SearchByNameResponse } from '../../models/account/searchByNameResponse';
import { SearchByEmailResponse } from '../../models/account/searchByEmailResponse';
import { SearchByPhoneNumberResponse } from '../../models/account/searchByPhoneNumberResponse';
import { SchoolService } from '../../services/school/school.service';
import { ListSchoolNameBySearchTextResponse } from '../../models/schools/listSchoolNameBySearchTextResponse';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { passwordValidator } from '../../utility/passwordValidator';
import { CalendarModule } from 'primeng/calendar';
import { RegisterSchoolStudentRequest } from '../../models/schools/registerSchoolStudentRequest';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SchoolStudentStatus } from '../../models/enums/schoolStudentStatus';
import { UserSelectors } from '../../states/user/user-selector';
import { filter, of, switchMap, take } from 'rxjs';
import { Store } from '@ngxs/store';
import { UserStateModel } from '../../states/user/user-state-model';
import { StudentListForSuperAdminResponse } from '../../models/super-admin/studentListForSuperAdminResponse';
import { UploadStudentDataComponent } from "./upload-student-data/upload-student-data.component";
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { InputSwitchModule, InputSwitchChangeEvent } from 'primeng/inputswitch';
import { StudentStatus } from '../../models/enums/schoolState';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../models/enums/courseLevelEnum';
import { ParentsInformationService } from '../../services/parents-information/parents-information.service';
import { ParentsInformationResponse } from '../../models/parents-information/parentsInformationResponse';
import { ParentsRelation, ParentsRelationDescriptions } from '../../models/enums/parentsRelation';
import { ShouldShowParentsInfoPipe } from './should-show-parents-info.pipe';
import { apiResponse } from '../../models/apiResponse';
import { StudentExportColumnRequest, StudentExportPreferences } from '../../models/super-admin/studentExportColumnRequest';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { PeerReviewService } from '../../services/peer-review/peer-review.service';

interface place {
  name: string
}
interface degreePreference {
  name: string,
  key: Course
}
interface DropdownOption {
  name: string;
  value: StudentStatus;
}

interface ColumnConfig {
  field: string;
  header: string;
  visible: boolean;
  width?: string;
}

@Component({
  selector: 'app-student-list',
  standalone: true,
  imports: [TableModule, ReactiveFormsModule, CalendarModule, PasswordModule, DialogModule, InputTextModule, CommonModule,
    DropdownModule, FormsModule, ButtonModule, AutoCompleteModule, ToggleButtonModule, UploadStudentDataComponent, MenuModule, OverlayPanelModule,
    InputSwitchModule, ProgressSpinnerModule, ShouldShowParentsInfoPipe, ConfirmDialogModule, TagModule],
  providers: [ConfirmationService],
  templateUrl: './student-list.component.html',
  styleUrl: './student-list.component.scss'
})
export class StudentListComponent implements OnInit {

  constructor(private superAdminService: SuperAdminService,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private utilityService: UtilityService,
    private schoolService: SchoolService,
    private store: Store,
    private router: Router,
    private parentsInformationService: ParentsInformationService,
    private confirmationService: ConfirmationService,
    private peerReviewService: PeerReviewService
  ) { }


  // Column visibility configuration
  columns: ColumnConfig[] = [
    { field: 'studentName', header: 'Student Name', visible: true },
    { field: 'gender', header: 'Gender', visible: true },
    { field: 'email', header: 'Email', visible: true },
    { field: 'phoneNumber', header: 'Phone Number', visible: true },
    { field: 'bornOn', header: 'DOB', visible: true },
    { field: 'highSchoolName', header: 'School Name', visible: true },
    { field: 'class', header: 'Class', visible: true },
    { field: 'section', header: 'Section', visible: true },
    { field: 'intendedMajor', header: 'Intended Major', visible: true },
    { field: 'degreePreference', header: 'Degree Preference', visible: true },
    { field: 'collageStartOn', header: 'College Start On', visible: true },
    { field: 'homeCountry', header: 'Home Country', visible: true },
    { field: 'destination', header: 'Destination', visible: true },
    { field: 'hearaboutus', header: 'Hear About Us', visible: true },
    { field: 'collegePreferences', header: 'College Preferences', visible: true },
    { field: 'journeyStatus', header: 'Journey Status', visible: true },
    { field: 'source', header: 'Source', visible: true },
    { field: 'initialJourneyActivities', header: 'Initial Journey Activities', visible: true },
    { field: 'actions', header: 'Actions', visible: true }
  ];

  columnMenuItems: MenuItem[] = [];
  readonly STORAGE_KEY = 'student-list-column-config';

  initializeForm() {
    this.filterForm = this.formBuilder.group({
      name: [null],
      destination: [null],
      highSchoolName: [null],
      homeCountry: [null],
      degreePreference: [null],
      studentState: [null],
      addedBy: [null],
      email: [null, Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')],
      phoneNumber: [null]
    });
   this.studentForm = this.formBuilder.group({
  firstName: ['', Validators.required],
  lastName: ['', Validators.required],
  gender: ['', Validators.required],
  bornOn: ['', Validators.required],
  email: ['', [Validators.required, Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')]],
  phoneNumber: ['', Validators.pattern('^[0-9]{10}$')],
  schoolName: [null],             
  class: [null],
  section: [null],
  admissionNumber: [null],
  assessmentType: [null],         
  newPassword: ['', [Validators.required, passwordValidator()]],
  confirmPassword: ['', Validators.required],
}, { validators: this.passwordsMatchValidator });

  }

  passwordsMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadCountries();

    if (this.router.url.includes('admin')) {
      this.portal = 'admin';
    } else if (this.router.url.includes('school-portal')) {
      this.portal = 'school-portal';
    } else if (this.router.url.includes('operations-portal')) {
      this.portal = 'operations-portal';
    }

    if (this.isOperationsPortal) {
      this.columns = this.columns.filter(c => c.field !== 'actions');
      const defaultOpsCols = ['studentName', 'email', 'phoneNumber', 'highSchoolName', 'degreePreference', 'journeyStatus'];
      this.columns.forEach(c => {
        if (!defaultOpsCols.includes(c.field)) {
          c.visible = false;
        }
      });
    }

    this.getSchoolStudentsCombined(1);
    this.genders = [
      { name: 'Male', value: 1 },
      { name: 'Female', value: 2 },
      { name: 'Other', value: 3 }
    ];
    this.addedBy = [
      { name: 'Added By Admin', value: true },
      { name: 'Organic', value: false }
    ]
    this.loadColumnConfiguration();
    this.updateColumnMenu();
    this.populateDropdownFromEnum();
  }

  portal: string = '';

  viewProfile(id: string): void {
    this.router.navigate([this.portal, 'view-profile', id]);
  }

  viewParentsInfo(id: string): void {
    this.selectedStudentId = id;
    this.parentsInfoVisible = true;
    this.parentsInfoLoading = true;
    this.parentsInfo = [];

    // Find student name for dialog header
    const student = this.studentList.find(s => s.id === id);
    this.selectedStudentName = student ? `${student.firstName} ${student.lastName}` : 'Student';

    this.parentsInformationService.getParentsInformationByStudentId(id).subscribe({
      next: (response) => {
        this.parentsInfo = response.result || [];
        this.parentsInfoLoading = false;
      },
      error: (error) => {
        this.parentsInfoLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load parents information'
        });
        console.error('Error loading parents information:', error);
      }
    });
  }

  // A converted student's email/username carries a ".converted-<guid>" marker
  // appended by the backend, so we can identify already-converted accounts.
  isStudentConverted(student: StudentListForSuperAdminResponse): boolean {
    return !!student.email && student.email.includes('.converted-');
  }

  convertStudentToPeer(studentId: string) {
    this.confirmationService.confirm({
      message: 'Warning: This will retire the student account and release its email and phone number ' +
        'so the person can sign up again as a Peer Reviewer. The student will no longer be able to log in ' +
        'with their current credentials. This action cannot be undone. Do you want to continue?',
      header: 'Convert Student to Peer',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Convert',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.isLoading = true;
        this.peerReviewService.convertStudentToPeer(studentId).subscribe({
          next: (response) => {
            this.isLoading = false;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Student converted to peer successfully' });
            this.getSchoolStudentsCombined(1);
          },
          error: (err) => {
            this.isLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to convert student to peer' });
          }
        });
      }
    });
  }


  // Load column configuration from localStorage
  private loadColumnConfiguration(): void {
    try {
      const savedConfig = localStorage.getItem(this.STORAGE_KEY);
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig);
        this.columns = this.columns.map(col => {
          if (col.field === 'studentName') {
            return { ...col, visible: true };
          }
          const savedCol = parsedConfig.find((c: ColumnConfig) => c.field === col.field);
          return savedCol ? { ...col, visible: savedCol.visible } : col;
        });
      }
    } catch (error) {
      console.warn('Error loading column configuration:', error);
    }
  }

  // Save column configuration to localStorage
  private saveColumnConfiguration(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.columns));
    } catch (error) {
      console.warn('Error saving column configuration:', error);
    }
  }

  // Toggle column visibility
  toggleColumnVisibility(field: string): void {
    if (field === 'studentName') return;
    const column = this.columns.find(col => col.field === field);
    if (column) {
      column.visible = !column.visible;
      this.saveColumnConfiguration();
      this.updateColumnMenu();
    }
  }

  // Handle column visibility change from InputSwitch
  onColumnVisibilityChange(event: InputSwitchChangeEvent, field: string): void {
    if (field === 'studentName') {
      setTimeout(() => {
        const column = this.columns.find(col => col.field === field);
        if (column) column.visible = true;
      });
      return;
    }
    const column = this.columns.find(col => col.field === field);
    if (column) {
      column.visible = event.checked;
      this.saveColumnConfiguration();
      this.updateColumnMenu();
    }
  }

  // Get visible columns
  get visibleColumns(): ColumnConfig[] {
    return this.columns.filter(col => col.visible);
  }

  // Update column menu items
  private updateColumnMenu(): void {
    this.columnMenuItems = this.columns.map(col => ({
      label: col.header,
      icon: col.visible ? 'pi pi-eye' : 'pi pi-eye-slash',
      command: () => this.toggleColumnVisibility(col.field),
      styleClass: col.visible ? 'text-green-600' : 'text-gray-400'
    }));
  }

  // Reset column configuration to default
  resetColumnConfiguration(): void {
    this.columns = this.columns.map(col => ({ ...col, visible: true }));
    if (this.isOperationsPortal) {
      const defaultOpsCols = ['studentName', 'email', 'phoneNumber', 'highSchoolName', 'degreePreference', 'journeyStatus'];
      this.columns.forEach(c => {
        if (!defaultOpsCols.includes(c.field)) {
          c.visible = false;
        }
      });
    }
    this.saveColumnConfiguration();
    this.updateColumnMenu();
  }

  // Helper method to check if a column is visible
  isColumnVisible(field: string): boolean {
    const column = this.columns.find(col => col.field === field);
    return column?.visible ?? false;
  }

  // Export current column configuration
  exportColumnConfiguration(): void {
    const config = {
      columns: this.columns,
      exportedAt: new Date().toISOString(),
      totalColumns: this.columns.length,
      visibleColumns: this.visibleColumns.length
    };

    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student-list-column-config-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);

    this.messageService.add({
      severity: 'success',
      summary: 'Configuration Exported',
      detail: 'Column configuration has been exported successfully.'
    });
  }

  // Import column configuration
  importColumnConfiguration(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const config = JSON.parse(e.target.result);
          if (config.columns && Array.isArray(config.columns)) {
            this.columns = config.columns;
            this.saveColumnConfiguration();
            this.updateColumnMenu();
            this.messageService.add({
              severity: 'success',
              summary: 'Configuration Imported',
              detail: 'Column configuration has been imported successfully.'
            });
          }
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Import Failed',
            detail: 'Invalid configuration file format.'
          });
        }
      };
      reader.readAsText(file);
    }
  }

  populateDropdownFromEnum() {
    this.stateOptions = Object.keys(StudentStatus).filter(key => isNaN(Number(key))).map(key => ({
      name: key,
      value: StudentStatus[key as keyof typeof StudentStatus]
    }));
  }

  stateOptions: DropdownOption[] = [];
  user: UserStateModel | undefined;
  names: SearchByNameResponse[] = []
  emails: SearchByEmailResponse[] = []
  phoneNumbers: SearchByPhoneNumberResponse[] = []
  submitted: boolean = false;

  get isAdminPortal(): boolean {
    return this.portal === 'admin';
  }

  get isOperationsPortal(): boolean {
    return this.portal === 'operations-portal';
  }

  viewDocuments(studentId: string): void {
    this.router.navigate(['operations-portal', 'student', studentId, 'details']);
  }
  countries: countryResponse[] = [];
  studentList: StudentListForSuperAdminResponse[] = [];
  genders: { name: string; value: number }[] = [];
  addedBy: { name: string; value: boolean }[] = [];
  totalRecords: number = 0;
  first: number = 0;
  rowSize: number = 10;
  pageSize = 10;
  filterForm!: FormGroup;
  studentForm!: FormGroup;
  selectedSchoolId!: number;
  assessmentTypes = [
    { name: AssessmentTypeDescriptions[AssessmentType.None], value: 0 },
    { name: AssessmentTypeDescriptions[AssessmentType.Stream], value: 1 },
    { name: AssessmentTypeDescriptions[AssessmentType.Career], value: 2 },
  ]
  degreePrefs = [
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree], key: CourseLevelEnum.BachelorsDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsAndMastersDegree], key: CourseLevelEnum.BachelorsAndMastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate], key: CourseLevelEnum.Certificate },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma], key: CourseLevelEnum.Diploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree], key: CourseLevelEnum.DoctoralDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DualDegree], key: CourseLevelEnum.DualDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.FoundationDegree], key: CourseLevelEnum.FoundationDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree], key: CourseLevelEnum.MastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.PostgraduateCertificate], key: CourseLevelEnum.PostgraduateCertificate },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.PostgraduateDiploma], key: CourseLevelEnum.PostgraduateDiploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.PreSessionalProgram], key: CourseLevelEnum.PreSessionalProgram },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.UndergraduateCertificate], key: CourseLevelEnum.UndergraduateCertificate },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.UndergraduateDiploma], key: CourseLevelEnum.UndergraduateDiploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Vocational], key: CourseLevelEnum.Vocational }
  ];

  schoolNames: ListSchoolNameBySearchTextResponse[] = [];
  isLoading: boolean = false;
  isVisible: boolean = false;
  readonly SchoolStudentStatus = SchoolStudentStatus;

  // Parents information dialog properties
  parentsInfoVisible: boolean = false;
  parentsInfo: ParentsInformationResponse[] = [];
  parentsInfoLoading: boolean = false;
  selectedStudentId: string = '';
  selectedStudentName: string = '';

  genderDescriptions(arg0: Gender | undefined): string {
    if (arg0) {
      return GenderDescriptions[arg0];
    }
    else {
      return '';
    }
  }

  getRelationDescription(relation: ParentsRelation): string {
    return ParentsRelationDescriptions[relation] || 'Unknown';
  }

  closeParentsInfoDialog(): void {
    this.parentsInfoVisible = false;
    this.parentsInfo = [];
    this.selectedStudentId = '';
    this.selectedStudentName = '';
  }

  filterSchoolNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length >= 3) {
      this.schoolService.listSchoolNameBySearchText(event.query).subscribe({
        next: (response) => {
          this.schoolNames = response.result
        }
      });
    } else {
      this.schoolNames = [];
    }
  }

  filterNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.names = response.result
        }
      });
    } else {
      this.names = [];
    }
  }

  filterEmails(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchEmailsByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.emails = response.result
        }
      });
    } else {
      this.emails = [];
    }
  }

  filterPhoneNumbers(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchPhoneNumbersByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.phoneNumbers = response.result
        }
      });
    } else {
      this.emails = [];
    }
  }

  private loadCountries(): void {
    this.utilityService.getCountries().subscribe({
      next: (response) => {
        if (response?.result) {
          this.countries = response.result;
        }
      },
      error: (err) => console.warn('Error loading countries:', err),
    });
  }

  getStudents(pageNumber: number) {
    if (pageNumber === 1) {
      this.first = 0;
    }
    this.isLoading = true;
    const studentListForSuperAdminRequest: StudentListForSuperAdminRequest = {
      studentName: this.filterForm.value.name,
      destination: this.filterForm.value.destination,
      highSchoolName: this.filterForm.value.highSchoolName,
      homeCountry: this.filterForm.value.homeCountry,
      degreePreference: this.filterForm.value.degreePreference,
      pageNumber: pageNumber,
      pageSize: this.rowSize,
      state: this.filterForm.value.studentState as StudentStatus,
      email: this.filterForm.value.email,
      phoneNumber: this.filterForm.value.phoneNumber,
      isAddedByAdmin: this.filterForm.value.addedBy
    }
    this.superAdminService.listStudents(studentListForSuperAdminRequest).subscribe({
      next: (response) => {
        this.studentList = response.result;
        this.totalRecords = response.total;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching students' });
      }
    })
  }
  getStudentsBySchool(pageNumber: number) {
    if (pageNumber === 1) {
      this.first = 0;
    }
    this.isLoading = true;
    const studentListForSuperAdminRequest: StudentListForSuperAdminRequest = {
      studentName: this.filterForm.value.name,
      destination: this.filterForm.value.destination,
      highSchoolName: this.filterForm.value.highSchoolName,
      homeCountry: this.filterForm.value.homeCountry,
      degreePreference: this.filterForm.value.degreePreference,
      createdBy: this.user?.id,
      pageNumber: pageNumber,
      pageSize: this.rowSize,
      state: this.filterForm.value.studentState as StudentStatus,
      isAddedByAdmin: this.filterForm.value.addedBy
    }
    this.superAdminService.listStudents(studentListForSuperAdminRequest).subscribe({
      next: (response) => {
        this.studentList = response.result;
        this.totalRecords = response.total;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching students' });
      }
    })
  }

  updateStudentStatus(id: number) {
    this.isLoading = true;
    this.schoolService.updateSchoolStudentStatus(id).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.getSchoolStudentsCombined(1);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Student status updated successfully' });
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating Student status' });
      }
    })
  }

  addStudent() {
    if (this.studentForm.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill all required fields correctly' });
      return;
    }

    this.isLoading = true;
    this.submitted = true;

    const formValue = this.studentForm.value;

    // Format date to YYYY-MM-DD
    let bornOnStr = formValue.bornOn;
    if (bornOnStr instanceof Date) {
      const year = bornOnStr.getFullYear();
      const month = String(bornOnStr.getMonth() + 1).padStart(2, '0');
      const day = String(bornOnStr.getDate()).padStart(2, '0');
      bornOnStr = `${year}-${month}-${day}`;
    }

    const registerSchoolStudentRequest: RegisterSchoolStudentRequest = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      gender: formValue.gender?.value,
      bornOn: bornOnStr,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      password: formValue.newPassword,
      confirmPassword: formValue.confirmPassword,
      highSchoolName: formValue.schoolName?.name || formValue.schoolName,
      schoolId: this.selectedSchoolId ?? 0,
      class: formValue.class,
      section: formValue.section,
      admissionNumber: formValue.admissionNumber,
      assessmentType: formValue.assessmentType,
    }

    this.schoolService.registerSchoolStudent(registerSchoolStudentRequest).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        this.studentForm.reset();
        this.closeDialog();
        this.getSchoolStudentsCombined(1);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Student added successfully' });
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding student' });
      }
    })
  }

  getSchoolStudentsCombined(pageNumber: number) {
    this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user && !!user.id),
      take(1),
      switchMap(user => {
        this.user = user;
        if (!this.portal) {
          const roles = user.role;
          if (roles.includes(AuthRoles.SuperAdmin) || roles.includes(AuthRoles.Admin)) {
            this.portal = 'admin';
          } else if (roles.includes(AuthRoles.SchoolAdmin)) {
            this.portal = 'school-portal';
          } else if (roles.includes(AuthRoles.Operations)) {
            this.portal = 'operations-portal';
          }
        }
        return of(user); // Ensure an observable is returned
      })
    ).subscribe({
      next: (response) => {
        if (response != null) {
          if (response.role.includes(AuthRoles.SuperAdmin) || response.role.includes(AuthRoles.Admin) || response.role.includes(AuthRoles.Operations)) {
            this.getStudents(pageNumber);
          }
          else if (response.role.includes(AuthRoles.SchoolAdmin)) {
            this.getStudentsBySchool(pageNumber);
          }
        }
      },
      error: (error) => {
        console.warn('Error fetching user data', error);
      }
    })
  }

  getHighSchoolId(event: any) {


  const school = event?.value ?? event;
  this.selectedSchoolId = school?.id;

}

  // Custom Export Dialog Logic: Started
  exportDialogVisible: boolean = false;

  exportableColumns = [
    { label: 'First Name', key: 'firstName', selected: false },
    { label: 'Last Name', key: 'lastName', selected: false },
    { label: 'Gender', key: 'gender', selected: false },
    { label: 'DOB', key: 'bornOn', selected: false },
    { label: 'Email', key: 'email', selected: false },
    { label: 'Phone Number', key: 'phoneNumber', selected: false },
    { label: 'School Name', key: 'highSchoolName', selected: false },
    { label: 'Status', key: 'status', selected: false },
    { label: 'Class', key: 'class', selected: false },
    { label: 'Section', key: 'section', selected: false },
    { label: 'Intended Major', key: 'intendedMajor', selected: false },
    { label: 'Degree Preference', key: 'degreePreference', selected: false },
    { label: 'College Start On', key: 'collageStartOn', selected: false },
    { label: 'Home Country', key: 'homeCountry', selected: false },
    { label: 'College Preferences', key: 'collagePreferences', selected: false },
    { label: 'Destination', key: 'destination', selected: false },
    { label: 'Hear About Us', key: 'hearaboutus', selected: false },
    { label: 'Journey Status', key: 'journeyStatus', selected: false },
    { label: 'Source', key: 'source', selected: false },
    { label: 'Initial Activities', key: 'initialJourneyActivities', selected: false },
    { label: 'Sign Up On', key: 'createdOn', selected: false },
    { label: 'Added By', key: 'addedBy', selected: false },
    { label: 'Parent Info', key: 'parentInfo', selected: false }
  ];

  showExportDialog() {
    this.exportDialogVisible = true;
  }

  get isAnyColumnSelected(): boolean {
    return this.exportableColumns.some(col => col.selected);
  }

  toggleAllExportColumns(event: any) {
    const isChecked = event.checked;
    this.exportableColumns.forEach(col => col.selected = isChecked);
  }

  // Export dialogs
  isExportLoading = false;

  downloadCustomColumnExcel() {
    this.isExportLoading = true;

    // Build the preferences object dynamically
    const preferences: any = {};
    this.exportableColumns.forEach(col => {
      preferences[col.key] = col.selected;
    });

    const request: StudentExportColumnRequest = {
      // Existing filters
      studentName: this.filterForm.value.name,
      destination: this.filterForm.value.destination,
      highSchoolName: this.filterForm.value.highSchoolName,
      homeCountry: this.filterForm.value.homeCountry,
      degreePreference: this.filterForm.value.degreePreference,
      state: this.filterForm.value.studentState as StudentStatus,
      isAddedByAdmin: this.filterForm.value.addedBy,
      email: this.filterForm.value.email,
      phoneNumber: this.filterForm.value.phoneNumber,
      // New Preferences
      exportPreferences: preferences as StudentExportPreferences
    };

    this.schoolService.downloadCustomStudentListOnSuperAdmin(request).subscribe({
      next: (resp) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd) || `Lead_Quality_Report_${new Date().toLocaleString('sv-SE').replace(/[: ]/g, '-').replace(',', '')}.xlsx`;
        const blob = new Blob([resp.body!], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.saveBlobAsFile(blob, filename);

        this.isExportLoading = false;
        this.exportDialogVisible = false; // Close dialog on success
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File downloaded successfully' });
      },
      error: (err) => {
        this.isExportLoading = false;
        console.error('Download failed', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download file' });
      }
    });
  }
  // Custom Export Dialog Logic: Ended

  downloadStudentListExcel() {
    const studentListForSuperAdminRequest: StudentListForSuperAdminRequest = {
      studentName: this.filterForm.value.name,
      destination: this.filterForm.value.destination,
      highSchoolName: this.filterForm.value.highSchoolName,
      homeCountry: this.filterForm.value.homeCountry,
      degreePreference: this.filterForm.value.degreePreference,
      state: this.filterForm.value.studentState as StudentStatus,
      isAddedByAdmin: this.filterForm.value.addedBy
    }

    this.schoolService.downloadSudentListOnSuperAdmin(studentListForSuperAdminRequest).subscribe({
      next: (resp) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd) || 'student_list.xlsx';
        const blob = new Blob([resp.body!], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.saveBlobAsFile(blob, filename);
      },
      error: (err) => console.error('Download failed', err)
    });
  }

  downloadExcel() {
    this.schoolService.downloadSampleExcelForStudentImport().subscribe({
      next: (resp) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd) || 'sample_student_import.xlsx';
        const blob = new Blob([resp.body!], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.saveBlobAsFile(blob, filename);
      },
      error: (err) => console.error('Download failed', err)
    });
  }
  getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches.length > 1) {
        return matches[1].replace(/['"]/g, '');
      }
    }
    return null;
  }
  saveBlobAsFile(blob: Blob | null, filename: string): void {
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }

  showDialog() {
    this.isVisible = true;
  }
  closeDialog() {
  this.isVisible = false;
  this.selectedSchoolId = 0; 
  this.studentForm.reset();
}

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.getSchoolStudentsCombined(pageNumber);
  }

  parseDate(dateString: string | null | undefined): Date | null {
    if (!dateString) return null;

    // Handle "DD/MM/YYYY" format
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const parts = dateString.split('/');
      // parts[0] is day, parts[1] is month, parts[2] is year
      return new Date(+parts[2], +parts[1] - 1, +parts[0]);
    }

    // Fallback for other formats
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

}